import { useState, type RefObject } from "react";
import dayjs from "dayjs";
import { useAppContext } from "@components/AppContext";
import type { CreateScheduleInputMutation, UpdateScheduleInputMutation } from "@api/types";
import type { BeneficiariesTableHandle } from "../../BeneficiariesTable";
import type { AppContext, ScheduleFormValues } from "../../types";

type Props = {
  beneficiariesTableRef: RefObject<BeneficiariesTableHandle | null>;
  scheduleId?: string;
  createSchedule: (mutation: CreateScheduleInputMutation) => Promise<unknown>;
  updateSchedule: (scheduleId: string, mutation: UpdateScheduleInputMutation) => Promise<unknown>;
  onClose: () => void;
};

const useFinish = ({
  onClose,
  scheduleId,
  createSchedule,
  updateSchedule,
  beneficiariesTableRef,
}: Props) => {
  const [ isLoading, setIsLoading ] = useState(false);
  const { showErrorMessage } = useAppContext() as AppContext;

  const onFinish = async (payload: ScheduleFormValues) => {
    const totalAmount = beneficiariesTableRef.current?.getTotalAmount();
    const selectedDistributions = beneficiariesTableRef.current?.getSelectedDistributions();

    const distributions = selectedDistributions
      ?.map((distribution) => ({
        ...distribution,
        transactionType: payload.transactionType,
      }));

    setIsLoading(true);

    try {
      const DATE_FORMAT = "YYYY-MM-DD";

      const mutation = {
        ...payload,
        startDate: dayjs(payload.startDate).format(DATE_FORMAT),
        nextDate: dayjs(payload.nextDate).format(DATE_FORMAT),
        totalAmount,
        distributions,
      };

      if (scheduleId) {
        await updateSchedule(scheduleId, mutation);

      } else {
        await createSchedule(mutation);

      }

      onClose();

    } catch (error) {
      showErrorMessage(error instanceof Error ? error.message : String(error));

    } finally {
      setIsLoading(false);

    }
  };

  return { onFinish, isLoading };
};

export default useFinish;
